import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { mangaList } from "@/mocks/manga";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setFocus(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setFocus(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = query.trim()
    ? mangaList.filter((m) => m.title.includes(query.trim())).slice(0, 5)
    : [];

  const goSearch = (id?: string) => {
    if (id) {
      navigate(`/detail/${id}`);
    } else if (results.length > 0) {
      navigate(`/detail/${results[0].id}`);
    }
    setFocus(false);
  };

  const navLinks = [
    { label: "首页", to: "/" },
    { label: "热门榜单", to: "/#hot" },
    { label: "最新更新", to: "/#new" },
    { label: "编辑精选", to: "/#pick" },
    { label: "剧情解读", to: "/#stories" },
    { label: "关于我们", to: "/about" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] w-full max-w-[1400px] items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-heart-3-fill text-lg md:text-xl"></i>
          </div>
          <Link to="/" className="font-display text-2xl md:text-[28px] leading-none text-primary-600">
            桃桃漫画网
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.to}
              className={`rounded-full px-4 py-2 text-[15px] transition-colors whitespace-nowrap ${
                (link.to === "/" && location.pathname === "/") ||
                (link.to !== "/" && location.pathname === link.to.split("#")[0])
                  ? "text-primary-600 font-medium"
                  : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div ref={searchRef} className="relative hidden sm:block">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                goSearch();
              }}
              className="relative"
            >
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setFocus(true);
                }}
                onFocus={() => setFocus(true)}
                placeholder="搜索作品"
                className="w-40 md:w-56 rounded-full border border-background-300/70 bg-background-50 py-2 pl-9 pr-4 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </form>
            {focus && query.trim() && (
              <div className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-lg">
                {results.length > 0 ? (
                  results.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => goSearch(r.id)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-primary-50"
                    >
                      <img src={r.cover} alt={r.title} className="h-12 w-9 rounded-md object-cover" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground-950">{r.title}</span>
                        <span className="block text-xs text-foreground-500">{r.categoryName} · {r.latestChapter}</span>
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-3 text-sm text-foreground-500">没有找到「{query}」相关作品</p>
                )}
              </div>
            )}
          </div>

          <Link
            to="/#hot"
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            立即开看
            <i className="ri-arrow-right-line"></i>
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-full border border-background-300/70 text-foreground-800"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-2">
          <div className="relative mb-3 sm:hidden">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索作品"
              className="w-full rounded-full border border-background-300/70 bg-background-50 py-2 pl-9 pr-4 text-sm outline-none focus:border-primary-400"
            />
          </div>
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.to}
              className="block rounded-lg px-3 py-2.5 text-[15px] text-foreground-800 hover:bg-primary-50 hover:text-primary-600"
            >
              {link.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              goSearch();
              setMobileOpen(false);
            }}
            className="mt-2 w-full rounded-full bg-primary-500 py-2.5 text-center text-sm font-medium text-background-50"
          >
            立即开看
          </button>
        </div>
      )}
    </header>
  );
}